import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { IconRecord, IconStop } from '../../../base/icons/svg';
import { MEET_FEATURES } from '../../../base/jwt/constants';
import { JitsiRecordingConstants } from '../../../base/lib-jitsi-meet';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import { maybeShowPremiumFeatureDialog } from '../../../jaas/actions';
import { getActiveSession, getRecordButtonProps } from '../../functions';
import LocalRecordingManager from './LocalRecordingManager';
import { showRecordingError } from '../../actions.any';
/**
 * An abstract implementation of a button for starting and stopping recording.
 */
export default class AbstractRecordButton extends AbstractButton {
    accessibilityLabel = 'dialog.startRecording';
    toggledAccessibilityLabel = 'dialog.stopRecording';
    icon = IconRecord;
    label = 'dialog.startRecording';
    toggledLabel = 'dialog.stopRecording';
    toggledIcon = IconStop;
    /**
     * Returns the tooltip that should be displayed when the button is disabled.
     *
     * @private
     * @returns {string}
     */
    _getTooltip() {
        return this.props._tooltip ?? '';
    }
    /**
     * Helper function to be implemented by subclasses, which should be used
     * to handle the start recoding button being clicked / pressed.
     *
     * @protected
     * @returns {void}
     */
    _onHandleClick() {
        // To be implemented by subclass.
    }
    /**
     * Handles clicking / pressing the button.
     *
     * @override
     * @protected
     * @returns {void}
     */
    async _handleClick() {
        const { _isRecordingRunning, dispatch } = this.props;
        let enable_vmeet_recording, remaining_recording_limit;
        if (_isRecordingRunning) {
            sendAnalytics(createToolbarEvent('recording.button', {
                'is_recording': _isRecordingRunning,
                type: JitsiRecordingConstants.mode.FILE
            }));
            const dialogShown = await dispatch(maybeShowPremiumFeatureDialog(MEET_FEATURES.RECORDING));
            if (!dialogShown) {
                this._onHandleClick();
            }
        }
        else {
            // const res = await IISightAPI.getConferenceOptions(window.APP?.room?.id);
            // const { data: body } = res
            enable_vmeet_recording = window.APP?.room?.conference_options?.enable_vmeet_recording;
            remaining_recording_limit = window.APP?.room?.conference_options?.remaining_recording_limit;
            if (remaining_recording_limit == 0) {
                dispatch(showRecordingError({
                    descriptionKey: 'recording.upgradeSubscription',
                    titleKey: 'recording.unavailableTitle'
                }));
            }
            else {
                sendAnalytics(createToolbarEvent('recording.button', {
                    'is_recording': _isRecordingRunning,
                    type: JitsiRecordingConstants.mode.FILE
                }));
                const dialogShown = await dispatch(maybeShowPremiumFeatureDialog(MEET_FEATURES.RECORDING));
                if (!dialogShown) {
                    this._onHandleClick();
                }
            }
        }
    }
    /**
     * Helper function to be implemented by subclasses, which must return a
     * boolean value indicating if this button is disabled or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isDisabled() {
        return this.props._disabled;
    }
    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._isRecordingRunning;
    }
}
/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code RecordButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _disabled: boolean,
 *     _isRecordingRunning: boolean,
 *     _tooltip: string,
 *     visible: boolean
 * }}
 */
export function _mapStateToProps(state) {
    const { disabled: _disabled, tooltip: _tooltip, visible } = getRecordButtonProps(state);
    // [STABILITY] Emergency UI Blackout: Force hide if we know a stop is active
    let isStopActive = false;
    try {
        isStopActive = localStorage.getItem('__11sight_recording_manually_stopped') === 'true';
    }
    catch (e) { }
    return {
        _disabled,
        _isRecordingRunning: !isStopActive && (Boolean(getActiveSession(state, JitsiRecordingConstants.mode.FILE))
            || LocalRecordingManager.isRecordingLocally()),
        _tooltip,
        visible: visible // Don't hide, just switch state to 'Start'
    };
}
